import {
  createContext,
  type InputHTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import {
  ACCENT_ROWS,
  DIGIT_ROW,
  EMAIL_SYMBOLS,
  LETTER_ROWS,
  TEXT_SYMBOLS,
  applyKey,
  emailShortcuts,
  startsWord,
  type KeyAction,
  type KeyboardMode,
} from './keyboard';

interface Field {
  mode: KeyboardMode;
  value: string;
  set: (v: string) => void;
  max?: number;
  /** Typed with a keypad shown right under the field (kiosk v2 phone number), not at the bottom. */
  inline?: boolean;
}
type FieldRef = MutableRefObject<Field>;

interface KeyboardApi {
  attach(ref: FieldRef): void;
  detach(ref: FieldRef): void;
  close(): void;
  active: FieldRef | null;
}

const Ctx = createContext<KeyboardApi | null>(null);

/** Holds which kiosk field the on-screen keyboard is typing into. */
export function KeyboardProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<FieldRef | null>(null);
  const attach = useCallback((ref: FieldRef) => setActive(ref), []);
  const detach = useCallback((ref: FieldRef) => setActive((a) => (a === ref ? null : a)), []);
  const close = useCallback(() => {
    setActive(null);
    (document.activeElement as HTMLElement | null)?.blur?.();
  }, []);
  const api = useMemo(() => ({ attach, detach, close, active }), [attach, detach, close, active]);
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

/**
 * Props for a kiosk <input>: the iPad keyboard is suppressed (`inputMode="none"`) and our own
 * keyboard opens on focus. A hardware barcode scanner still types into the field as before.
 */
export function useKeyboardInput(
  mode: KeyboardMode,
  value: string,
  set: (v: string) => void,
  max?: number,
  opts: { inline?: boolean } = {},
) {
  const kb = useContext(Ctx);
  const ref = useRef<Field>({ mode, value, set, max, inline: opts.inline });
  ref.current = { mode, value, set, max, inline: opts.inline };
  // `detach` is stable; depending on `kb` would detach the field every time the keyboard opens.
  const detach = kb?.detach;
  useEffect(() => () => detach?.(ref), [detach]);
  // No kiosk keyboard (the phone page): the phone's own keyboard, of the right kind.
  if (!kb)
    return { inputMode: mode === 'digits' ? ('numeric' as const) : mode === 'email' ? ('email' as const) : undefined };
  return {
    inputMode: 'none' as const,
    'data-kb-active': kb.active === ref ? 'true' : undefined,
    onFocus: (e: FocusEvent<HTMLElement>) => {
      kb.attach(ref);
      const el = e.currentTarget;
      // Keep the field visible above the keyboard.
      window.setTimeout(() => el.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 50);
    },
  };
}

export function useKeyboardOpen(): boolean {
  return Boolean(useContext(Ctx)?.active);
}

/** Closes the kiosk keyboard (e.g. once an answer makes typing unnecessary); no-op without one. */
export function useKeyboardClose(): () => void {
  const kb = useContext(Ctx);
  return kb?.close ?? (() => undefined);
}

/**
 * The keyboard itself; placed in the kiosk layout above the footer.
 * Kiosk v2 (owner 17.10.2026): the phone keypad sits compact right under the phone field
 * (`inline` slot next to the field); the bottom slot then `skipInline`s those fields.
 */
export function KeyboardSlot({
  labels,
  inline = false,
  skipInline = false,
}: {
  labels: { done: string; space: string };
  inline?: boolean;
  skipInline?: boolean;
}) {
  const kb = useContext(Ctx);
  const [shift, setShift] = useState(false);
  const [accents, setAccents] = useState(false);
  // The field's value after the last key (the field re-renders after the slot).
  const [shown, setShown] = useState('');
  const field = kb?.active?.current;
  useEffect(() => {
    setShift(false);
    setAccents(false);
    setShown(kb?.active?.current.value ?? '');
  }, [kb?.active]);
  const box = useRef<HTMLDivElement>(null);
  const mine = Boolean(field) && (inline ? Boolean(field!.inline) : !(skipInline && field!.inline));
  const open = Boolean(kb?.active) && mine;
  // Sheets (country picker) lift above the keyboard via --kb-h.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = box.current;
    // an inline keypad sits in the page: sheets need not move for it
    if (inline) return;
    if (!open || !el) {
      root.style.setProperty('--kb-h', '0px');
      return;
    }
    const fit = () => root.style.setProperty('--kb-h', `${el.offsetHeight}px`);
    fit();
    const ro = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(fit);
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.setProperty('--kb-h', '0px');
    };
  });
  if (!kb || !kb.active || !field || !mine) return null;
  const mode = field.mode;

  const press = (action: KeyAction) => {
    const f = kb.active!.current;
    const next = applyKey(f.value, action, f.mode, shift, f.max);
    f.value = next;
    f.set(next);
    setShown(next);
    if (action.type === 'char') setShift(false);
  };
  // pointerdown + preventDefault: the input keeps focus while keys are tapped.
  const key = (label: ReactNode, action: KeyAction, cls = '', aria?: string) => (
    <button
      type="button"
      key={`${cls}-${aria ?? String(label)}`}
      className={`kb-key ${cls}`}
      aria-label={aria}
      onPointerDown={(e) => e.preventDefault()}
      onClick={() => press(action)}
    >
      {label}
    </button>
  );
  const upper = mode === 'text' && (shift || startsWord(shown));
  const letter = (c: string) => key(upper ? c.toUpperCase() : c, { type: 'char', value: c });
  const done = (
    <button
      type="button"
      className="kb-key kb-done"
      onPointerDown={(e) => e.preventDefault()}
      onClick={() => kb.close()}
    >
      {labels.done}
    </button>
  );
  const backspace = key('⌫', { type: 'backspace' }, 'kb-wide', 'Backspace');

  if (mode === 'digits') {
    return (
      <div className={inline ? 'kb kb-digits kb-inline' : 'kb kb-digits'} role="group" aria-label="Keyboard" ref={box}>
        <div className="kb-pad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => key(d, { type: 'char', value: d }))}
          {backspace}
          {key('0', { type: 'char', value: '0' })}
          {done}
        </div>
      </div>
    );
  }

  const shortcuts = mode === 'email' ? emailShortcuts(shown) : [];
  return (
    <div className={`kb kb-${mode}`} role="group" aria-label="Keyboard" ref={box}>
      {shortcuts.length > 0 && (
        <div className="kb-row kb-chips">{shortcuts.map((s) => key(s, { type: 'char', value: s }, 'kb-chip'))}</div>
      )}
      <div className="kb-row">{DIGIT_ROW.map((d) => key(d, { type: 'char', value: d }, 'kb-digit'))}</div>
      {(accents ? ACCENT_ROWS : LETTER_ROWS).map((row, i, rows) => (
        <div className="kb-row" key={i}>
          {i === 2 && mode === 'text' && (
            <button
              type="button"
              className={`kb-key kb-wide${shift ? ' on' : ''}`}
              aria-label="Shift"
              aria-pressed={shift}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => setShift(!shift)}
            >
              ⇧
            </button>
          )}
          {row.map(letter)}
          {i === rows.length - 1 && backspace}
        </div>
      ))}
      <div className="kb-row">
        {mode === 'text' && (
          <button
            type="button"
            className={`kb-key kb-wide${accents ? ' on' : ''}`}
            aria-pressed={accents}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => setAccents(!accents)}
          >
            {accents ? 'abc' : 'áç'}
          </button>
        )}
        {(mode === 'email' ? EMAIL_SYMBOLS : TEXT_SYMBOLS).map((c) => key(c, { type: 'char', value: c }, 'kb-sym'))}
        {mode === 'text' && key(labels.space, { type: 'char', value: ' ' }, 'kb-space')}
        {mode === 'email' && <span className="kb-gap" />}
        {done}
      </div>
    </div>
  );
}

/** A kiosk text field typed with the on-screen keyboard (`multiline` → textarea). */
export function KInput({
  mode,
  value,
  onValue,
  max,
  multiline,
  rows,
  ...rest
}: {
  mode: KeyboardMode;
  value: string;
  onValue: (v: string) => void;
  max?: number;
  multiline?: boolean;
  rows?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'maxLength' | 'inputMode'>) {
  const kb = useKeyboardInput(mode, value, onValue, max);
  const common = { value, maxLength: max, autoComplete: 'off', ...kb };
  if (multiline) {
    return (
      <textarea
        className={rest.className}
        placeholder={rest.placeholder}
        aria-label={rest['aria-label']}
        rows={rows}
        {...common}
        onChange={(e) => onValue(e.target.value)}
      />
    );
  }
  return <input {...rest} {...common} onChange={(e) => onValue(e.target.value)} />;
}
